import React, { useCallback, useEffect, useRef, useState } from "react";
import { hasTrue, limitInputLength } from "@/helper/helper";
import { debounce } from "lodash";
import { useConfig } from "@/states/common.state";
import { useUserEmail } from "@/states/qr_types/email.state";
import { useHandleGenerateResult } from "@/hooks/useHandleGenerateResult";
import { useSignUpModal } from "@/states/ui.state";
import useGenerateHomeQr from "@/components/page-modules/home-page/hooks/useGenerateHomeQr";
import classes from "./EmailHomepage.module.scss";
import { useTranslations } from "next-intl";
import { useMediaQuery } from "react-responsive";
import { useQrShowState } from "@/states/qr.state";
import TypeInput from "../_shared/TypeInput";
import TypeTextarea from "../_shared/TypeTextarea";
const Email = () => {
  const {
    handleGenerateResult
  } = useHandleGenerateResult();
  const {
    email,
    subject,
    body,
    isDownload,
    setParams
  } = useUserEmail();
  const {
    payload,
    config
  } = useConfig();
  const [immediateText, setImmediateText] = useState<string>("");
  const [isFirst, setIsFirst] = useState<boolean>(true);
  const {
    dashboardQr
  } = useQrShowState();
  const isMobile = useMediaQuery({
    maxWidth: 1025
  });
  const signupModalState = useSignUpModal();
  const {
    mutateHomeQr,
    mutateLoading,
    isSuccess
  } = useGenerateHomeQr();
  const [errors, setErrors] = useState<Array<string> | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations("QrTypes.email");
  useEffect(() => {
    const error = dashboardQr?.["validation_errors"]?.["email"] ?? null;
    if (error && window && signupModalState.isTriedDownload && !mutateLoading) {
      emailInputRef.current?.focus();
    }
  }, [signupModalState.isTriedDownload, mutateLoading]);
  const setDebouncedErrors = useCallback(debounce(newErrors => setErrors(newErrors), 500), []);
  useEffect(() => {
    if (hasTrue(hasTrue(!isFirst, signupModalState.isTriedDownload), isMobile)) {
      setDebouncedErrors(dashboardQr?.["validation_errors"]?.["email"] ?? null);
    }
  }, [payload, email, isMobile]);
  useEffect(() => {
    if (signupModalState.isTriedDownload) {
      queueMicrotask(() => {
        setErrors(null);
        setTimeout(() => {
          setDebouncedErrors(dashboardQr?.["validation_errors"]?.["email"] ?? null);
        }, 200);
      });
    }
  }, [signupModalState.isTempTrigger]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    if (name === "body") {
      setImmediateText(value);
    }
    setParams({
      [name]: value
    });
  };
  useEffect(() => {
    signupModalState.setIsTriedDownload(false);
    handleGenerateResult(null);
    return () => {
      signupModalState.setIsTriedDownload(false);
      setErrors(null);
    };
  }, []);
  useEffect(() => {
    if (!isFirst) mutateHomeQr({
      params: {
        email,
        subject,
        body,
        config
      }
    });
  }, [email, subject, body, isDownload, isFirst]);
  const optimizedFn = useCallback(debounce(handleChange, 500), []);
  return <div className={classes.Wrapper} data-sentry-component="Email" data-sentry-source-file="Email.tsx">
      <div className={classes.Header}>
        <span className={classes.step}>1</span>
        <p className={classes.TopTitle}>{t("title")}</p>
      </div>
      <div className={classes.Flex}>
        <div className={classes.InputBox}>
          <TypeInput isRequired label={t("emailInput.label")} inputRef={emailInputRef} name="email" type="text" errors={errors} placeholder={t("emailInput.placeholder")} defaultValue={email} onChange={e => {
          optimizedFn.call(null, e), setIsFirst(false);
        }} data-sentry-element="TypeInput" data-sentry-source-file="Email.tsx" />
          <TypeInput label={t("subject.label")} name="subject" type="text" placeholder={t("subject.placeholder")} defaultValue={subject} onChange={e => optimizedFn.call(null, e)} data-sentry-element="TypeInput" data-sentry-source-file="Email.tsx" />
        </div>
        <TypeTextarea label={t("textarea.label")} name="body" placeholder={t("textarea.placeholder")} defaultValue={body} onChange={e => optimizedFn.call(null, e)} onInput={e => {
        limitInputLength(e, 500);
      }} maxLength={500} data-sentry-element="TypeTextarea" data-sentry-source-file="Email.tsx" />
      </div>
    </div>;
};
export default Email;